import { assertCanHandle, isNil } from '@lucidlink/tscore';
import { Logging } from '#common/logging';
import { withStats } from '#common/stats';
import { FilespaceEpochError } from '#common/types';
import { MetaStoreStats } from '#metastore/types';
import { ApplyDataRequest, FetchDataRequest, InvokeProcedureRequest, } from './messages';
export class EpochInfo {
    nodeId = -1;
    epoch = 0n;
    connection = 0n;
}
export class MetaStoreProxy {
    client;
    epochManager;
    serverSession;
    keyStoreServiceDestinationPath;
    log = Logging.getLogger('MetaStoreProxy');
    statistics = new MetaStoreStats(this.log);
    constructor(fsId, client, epochManager, serverSession) {
        this.client = client;
        this.epochManager = epochManager;
        this.serverSession = serverSession;
        this.keyStoreServiceDestinationPath = `/${fsId}/KeyStoreService`;
    }
    async fetchData(rangeStart, rangeLimit, maxKeys, maxSize, cursor) {
        return await withStats(this.statistics.fetchData, async () => {
            const req = new FetchDataRequest(rangeStart, rangeLimit, maxKeys, maxSize, this.keyStoreServiceDestinationPath, cursor);
            const result = (await this.sendRequest(req)).result;
            return result;
        });
    }
    async applyData(rangeStart, rangeLimit, data, startTime, currentTime) {
        return await withStats(this.statistics.applyData, async () => {
            const epochCounter = this.epochManager.incrementIfAcknowledged();
            const result = await this.sendApplyDataGuarded(rangeStart, rangeLimit, data, startTime, currentTime, this.prepareEpochData(epochCounter));
            this.epochManager.acknowledge();
            return result;
        }, { numKeyValuePairs: data.length });
    }
    async invokeProcedure(name, args, maxKeys, maxSize, cursor) {
        return await withStats(this.statistics.invokeProcedure, async () => {
            const req = new InvokeProcedureRequest(name, args, maxKeys, maxSize, this.keyStoreServiceDestinationPath, cursor);
            const result = (await this.sendRequest(req)).result;
            return result;
        }, { name });
    }
    stats() {
        return this.statistics;
    }
    prepareEpochData(epoch) {
        const nodeId = this.epochManager.getNodeId();
        const connection = this.epochManager.getHubConnection();
        const epochData = {
            nodeId,
            epoch,
            connection: connection,
        };
        return epochData;
    }
    async sendApplyDataGuarded(rangeStart, rangeLimit, data, startTime, currentTime, epoch) {
        try {
            return await this.doSendApplyData(rangeStart, rangeLimit, data, startTime, currentTime, epoch);
        }
        catch (err) {
            assertCanHandle(err, (e) => e instanceof FilespaceEpochError);
            // We handle the epoch error asynchronously so that the subsequent filespace unlink call
            // doesn't deadlock with the current applyData processing
            this.epochManager.handleRuntimeEpochError(err).catch(() => { });
            throw err;
        }
    }
    async doSendApplyData(rangeStart, rangeLimit, data, startTime, currentTime, epoch) {
        const req = new ApplyDataRequest(rangeStart, rangeLimit, data, startTime, currentTime, epoch.nodeId, epoch.epoch, epoch.connection, this.keyStoreServiceDestinationPath);
        return (await this.sendRequest(req)).result;
    }
    async establishSession() {
        if (isNil(this.serverSession)) {
            return undefined;
        }
        return await this.serverSession.getSessionToken();
    }
    async sendRequest(msg) {
        const sessionToken = await this.establishSession();
        return (await this.client.sendRequest(msg, sessionToken));
    }
}
